import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, chiusureApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CalendarDays, 
  TrendingUp, 
  TrendingDown,
  Users,
  Banknote,
  CreditCard,
  Receipt,
  AlertTriangle,
  Download,
  Upload,
  Loader2,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  CalendarRange,
  History,
  Sun,
  FileText,
  Calculator
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, subYears, getDaysInMonth, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { cn } from '../lib/utils';

const COLORS = ['#D94F3D', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [prevYearStats, setPrevYearStats] = useState(null);
  const [dataInizio, setDataInizio] = useState(startOfMonth(new Date()));
  const [dataFine, setDataFine] = useState(new Date());
  const [confronta, setConfronta] = useState(false);
  const [calendarOpenInizio, setCalendarOpenInizio] = useState(false);
  const [calendarOpenFine, setCalendarOpenFine] = useState(false);
  const [activeFilter, setActiveFilter] = useState('this-month');
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFilename, setExportFilename] = useState('');

  useEffect(() => {
    loadStats();
  }, [dataInizio, dataFine, confronta, activeFilter]);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Load current period stats
      const data = await dashboardApi.getStats(
        format(dataInizio, 'yyyy-MM-dd'),
        format(dataFine, 'yyyy-MM-dd'),
        confronta
      );
      setStats(data);
      
      // Load previous year same period stats for comparison
      if (activeFilter === 'this-month' || activeFilter === 'this-year') {
        const prevYearStart = subYears(dataInizio, 1);
        const prevYearEnd = subYears(dataFine, 1);
        const prevData = await dashboardApi.getStats(
          format(prevYearStart, 'yyyy-MM-dd'),
          format(prevYearEnd, 'yyyy-MM-dd'),
          false
        );
        setPrevYearStats(prevData);
      } else {
        setPrevYearStats(null);
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  const setQuickFilter = (type) => {
    const today = new Date();
    setActiveFilter(type);
    switch (type) {
      case 'this-month':
        setDataInizio(startOfMonth(today));
        setDataFine(today);
        break;
      case 'this-year':
        setDataInizio(startOfYear(today));
        setDataFine(today);
        break;
      case 'last-month':
        const lastMonth = subMonths(today, 1);
        setDataInizio(startOfMonth(lastMonth));
        setDataFine(endOfMonth(lastMonth));
        break;
      case 'last-year':
        const lastYear = subYears(today, 1);
        setDataInizio(startOfYear(lastYear));
        setDataFine(new Date(lastYear.getFullYear(), 11, 31));
        break;
      default:
        break;
    }
  };

  const openExportDialog = () => {
    const defaultName = `chiusure_${format(dataInizio, 'yyyy-MM-dd')}_${format(dataFine, 'yyyy-MM-dd')}`;
    setExportFilename(defaultName);
    setShowExportDialog(true);
  };

  const handleExportXlsx = async () => {
    if (!exportFilename.trim()) {
      toast.error('Inserisci un nome per il file');
      return;
    }
    
    setExporting(true);
    setShowExportDialog(false);
    try {
      const blob = await chiusureApi.exportXlsx(
        format(dataInizio, 'yyyy-MM-dd'),
        format(dataFine, 'yyyy-MM-dd')
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportFilename.trim()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`File "${exportFilename}.xlsx" scaricato!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const calculateVariation = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const VariationBadge = ({ value }) => {
    if (value === 0) {
      return (
        <span className="flex items-center text-muted-foreground text-sm">
          <Minus className="w-4 h-4 mr-1" />
          0%
        </span>
      );
    }
    const isPositive = value > 0;
    return (
      <span className={cn(
        "flex items-center text-sm font-medium",
        isPositive ? "text-green-600" : "text-red-600"
      )}>
        {isPositive ? (
          <ArrowUpRight className="w-4 h-4 mr-1" />
        ) : (
          <ArrowDownRight className="w-4 h-4 mr-1" />
        )}
        {isPositive ? '+' : ''}{value}%
      </span>
    );
  };

  // Get period label for comparison
  const getPrevYearPeriodLabel = () => {
    if (activeFilter === 'this-month') {
      return format(subYears(dataInizio, 1), 'MMMM yyyy', { locale: it });
    } else if (activeFilter === 'this-year') {
      return format(subYears(dataInizio, 1), 'yyyy');
    }
    return '';
  };

  // Prepare chart data
  const weekdayChartData = stats ? [
    { name: 'LUN', incasso: stats.incasso_per_giorno.LUN },
    { name: 'MAR', incasso: stats.incasso_per_giorno.MAR },
    { name: 'MER', incasso: stats.incasso_per_giorno.MER },
    { name: 'GIO', incasso: stats.incasso_per_giorno.GIO },
    { name: 'VEN', incasso: stats.incasso_per_giorno.VEN },
    { name: 'SAB', incasso: stats.incasso_per_giorno.SAB },
    { name: 'DOM', incasso: stats.incasso_per_giorno.DOM },
  ] : [];

  const paymentChartData = stats ? [
    { name: 'Contanti', value: stats.totale_contanti, percent: stats.percentuale_contanti },
    { name: 'POS', value: stats.totale_pos, percent: stats.percentuale_pos },
    { name: 'Bonifico', value: stats.totale_bonifico, percent: stats.percentuale_bonifico },
    { name: 'Assegno', value: stats.totale_assegno, percent: stats.percentuale_assegno },
    { name: 'Buoni', value: stats.totale_buoni_pasto, percent: stats.percentuale_buoni_pasto },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold font-heading tracking-tight">Dashboard</h1>
          </div>
          
          {/* Quick Filters - Updated */}
          <div className="flex flex-wrap gap-2 mb-3">
            <Button 
              variant={activeFilter === 'this-month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setQuickFilter('this-month')}
              data-testid="filter-this-month"
            >
              <CalendarRange className="w-4 h-4 mr-1" />
              Questo mese
            </Button>
            <Button 
              variant={activeFilter === 'this-year' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setQuickFilter('this-year')}
              data-testid="filter-this-year"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Quest'anno
            </Button>
            <Button 
              variant={activeFilter === 'last-year' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setQuickFilter('last-year')}
              data-testid="filter-last-year"
            >
              <History className="w-4 h-4 mr-1" />
              Anno scorso
            </Button>
          </div>

          {/* Date Range - With year shown */}
          <div className="flex items-center gap-2">
            <Popover open={calendarOpenInizio} onOpenChange={setCalendarOpenInizio}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1" data-testid="date-from">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  {format(dataInizio, 'd MMM yyyy', { locale: it })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dataInizio}
                  onSelect={(d) => { 
                    if(d) { 
                      setDataInizio(d); 
                      setCalendarOpenInizio(false); 
                      setActiveFilter('custom');
                    }
                  }}
                  locale={it}
                />
              </PopoverContent>
            </Popover>
            <span className="text-muted-foreground">→</span>
            <Popover open={calendarOpenFine} onOpenChange={setCalendarOpenFine}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1" data-testid="date-to">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  {format(dataFine, 'd MMM yyyy', { locale: it })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dataFine}
                  onSelect={(d) => { 
                    if(d) { 
                      setDataFine(d); 
                      setCalendarOpenFine(false);
                      setActiveFilter('custom');
                    }
                  }}
                  locale={it}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Export/Import buttons */}
          <div className="flex items-center justify-end mt-3 gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={openExportDialog}
              disabled={exporting}
              data-testid="export-xlsx-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Esporta XLSX
            </Button>
            {isAdmin() && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/import')}
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Import
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Esporta Chiusure
            </DialogTitle>
            <DialogDescription>
              Scegli il nome del file Excel da scaricare
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="filename">Nome file</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="filename"
                  value={exportFilename}
                  onChange={(e) => setExportFilename(e.target.value)}
                  placeholder="nome_file"
                />
                <span className="text-muted-foreground">.xlsx</span>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Periodo: {format(dataInizio, 'd MMM yyyy', { locale: it })} - {format(dataFine, 'd MMM yyyy', { locale: it })}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleExportXlsx}>
              <Download className="w-4 h-4 mr-2" />
              Scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !stats || stats.giorni_nel_periodo === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Nessun dato disponibile</h3>
              <p className="text-muted-foreground">Non ci sono chiusure nel periodo selezionato</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Previous Year Comparison - Shown only for "Questo mese" or "Quest'anno" */}
            {prevYearStats && prevYearStats.giorni_nel_periodo > 0 && (activeFilter === 'this-month' || activeFilter === 'this-year') && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                    <History className="w-5 h-5" />
                    Confronto con {getPrevYearPeriodLabel()}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-blue-600 mb-1">Incasso attuale</p>
                      <p className="text-xl font-bold font-mono-numbers text-green-600">
                        {formatCurrency(stats.incasso_totale)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 mb-1">Incasso {getPrevYearPeriodLabel()}</p>
                      <p className="text-xl font-bold font-mono-numbers text-blue-700">
                        {formatCurrency(prevYearStats.incasso_totale)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                    <span className="text-sm text-blue-700">Variazione anno su anno</span>
                    <VariationBadge value={calculateVariation(stats.incasso_totale, prevYearStats.incasso_totale)} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Banknote className="w-4 h-4" />
                    <span className="text-xs">Incasso Totale</span>
                  </div>
                  <p className="text-2xl font-bold font-mono-numbers text-green-600" data-testid="incasso-totale">
                    {formatCurrency(stats.incasso_totale)}
                  </p>
                  {confronta && stats.confronto && (
                    <div className="mt-1">
                      <p className="text-xs text-muted-foreground">
                        Prec: {formatCurrency(stats.confronto.incasso_precedente)}
                      </p>
                      <VariationBadge value={stats.confronto.incasso_variazione} />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs">Totale Fatturato</span>
                  </div>
                  <p className="text-xl font-bold font-mono-numbers text-blue-700" data-testid="totale-fatturato">
                    {formatCurrency(stats.totale_fatturato || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Scontrini + Fatture
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-purple-600 mb-1">
                    <Calculator className="w-4 h-4" />
                    <span className="text-xs">Totale Proforma</span>
                  </div>
                  <p className="text-xl font-bold font-mono-numbers text-purple-700" data-testid="totale-proforma">
                    {formatCurrency(stats.totale_proforma || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cassa in Cloud - Scontr. - Fatt.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-xs">Coperti Totali</span>
                  </div>
                  <p className="text-2xl font-bold font-mono-numbers" data-testid="coperti-totali">
                    {stats.coperti_totali}
                  </p>
                  {confronta && stats.confronto && (
                    <div className="mt-1">
                      <p className="text-xs text-muted-foreground">
                        Prec: {stats.confronto.coperti_precedente}
                      </p>
                      <VariationBadge value={stats.confronto.coperti_variazione} />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <TrendingDown className="w-4 h-4" />
                    <span className="text-xs">Uscite Totali</span>
                  </div>
                  <p className="text-xl font-bold font-mono-numbers text-red-600" data-testid="uscite-totali">
                    {formatCurrency(stats.uscite_totali)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Receipt className="w-4 h-4" />
                    <span className="text-xs">Incasso Medio/Giorno</span>
                  </div>
                  <p className="text-xl font-bold font-mono-numbers" data-testid="incasso-medio">
                    {formatCurrency(stats.incasso_medio_giornaliero)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    su {stats.giorni_nel_periodo} giorni
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-xs">Spesa Media Coperto</span>
                  </div>
                  <p className="text-sm font-mono-numbers">
                    Pranzo: <span className="font-bold">{formatCurrency(stats.spesa_media_coperto_pranzo)}</span>
                  </p>
                  <p className="text-sm font-mono-numbers">
                    Cena: <span className="font-bold">{formatCurrency(stats.spesa_media_coperto_cena)}</span>
                  </p>
                  {confronta && stats.confronto && (
                    <div className="mt-1">
                      <VariationBadge value={stats.confronto.spesa_media_variazione} />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Card Media Giornaliera e Previsione Mensile */}
            {(() => {
              // Calcola i giorni solari trascorsi nel periodo
              const giorniSolariTrascorsi = differenceInDays(dataFine, dataInizio) + 1;
              const mediaGiornalieraSolare = giorniSolariTrascorsi > 0 
                ? stats.incasso_totale / giorniSolariTrascorsi 
                : 0;
              
              // Calcola previsione mensile solo se "Questo mese" è selezionato
              const isThisMonth = activeFilter === 'this-month';
              const giorniTotaliMese = isThisMonth ? getDaysInMonth(dataInizio) : 0;
              const previsioneMensile = isThisMonth 
                ? mediaGiornalieraSolare * giorniTotaliMese 
                : 0;
              
              return (
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-blue-700 mb-3">
                      <BarChart3 className="w-5 h-5" />
                      <span className="font-semibold">Media e Previsioni</span>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Media Incasso Giornaliero */}
                      <div className="bg-white/70 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Media Incasso Giornaliero
                        </p>
                        <p className="text-2xl font-bold font-mono-numbers text-blue-700">
                          {formatCurrency(mediaGiornalieraSolare)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Fatturato / {giorniSolariTrascorsi} giorni solari
                        </p>
                      </div>
                      
                      {/* Previsione Mensile - Solo per "Questo mese" */}
                      {isThisMonth && (
                        <div className="bg-white/70 rounded-lg p-3 border-l-4 border-green-500">
                          <p className="text-xs text-muted-foreground mb-1">
                            📈 Previsione Incasso Mensile
                          </p>
                          <p className="text-2xl font-bold font-mono-numbers text-green-600">
                            {formatCurrency(previsioneMensile)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Media ({formatCurrency(mediaGiornalieraSolare)}) × {giorniTotaliMese} giorni del mese
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Card Pausa Pranzo nella Spazio */}
            {stats.pranzi_lmgv_giorni > 0 && (
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-700 mb-3">
                    <Sun className="w-5 h-5" />
                    <span className="font-semibold">Pausa pranzo nella Spazio</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Dati dei soli pranzi nei giorni feriali (esclusi Mer, Sab, Dom)
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {/* Totali */}
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Fatturato Totale</p>
                      <p className="text-xl font-bold font-mono-numbers text-amber-700">
                        {formatCurrency(stats.pranzi_lmgv_fatturato_totale)}
                      </p>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Coperti Totali</p>
                      <p className="text-xl font-bold font-mono-numbers text-amber-700">
                        {stats.pranzi_lmgv_coperti_totali}
                      </p>
                    </div>
                    
                    {/* Medie - calcolate solo sui giorni aperti */}
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Fatturato Medio/Giorno</p>
                      <p className="text-lg font-bold font-mono-numbers text-orange-600">
                        {formatCurrency(stats.pranzi_lmgv_fatturato_medio)}
                      </p>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Coperti Medi/Giorno</p>
                      <p className="text-lg font-bold font-mono-numbers text-orange-600">
                        {stats.pranzi_lmgv_coperti_medi}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    {stats.pranzi_lmgv_giorni_aperti !== undefined && stats.pranzi_lmgv_giorni_aperti !== stats.pranzi_lmgv_giorni ? (
                      <>Medie calcolate su {stats.pranzi_lmgv_giorni_aperti} giorni aperti (su {stats.pranzi_lmgv_giorni} totali)</>
                    ) : (
                      <>Calcolato su {stats.pranzi_lmgv_giorni} giorni</>
                    )}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Uscite per Categoria */}
            {stats.uscite_totali > 0 && Object.keys(stats.uscite_per_categoria).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    Uscite per Categoria
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(stats.uscite_per_categoria)
                      .sort((a, b) => b[1] - a[1])
                      .map(([categoria, importo]) => (
                        <div key={categoria} className="flex items-center justify-between">
                          <span className="text-sm">{categoria}</span>
                          <span className="font-mono-numbers font-semibold text-red-600">
                            {formatCurrency(importo)}
                          </span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delta Fiscale Alert */}
            {stats.giorni_con_delta > 0 && (
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Anomalie Cassa Cloud</p>
                      <p className="text-sm text-amber-700">
                        {stats.giorni_con_delta} giorni con delta ≠ 0
                      </p>
                      <p className="text-sm text-amber-700">
                        Somma assoluta delta: <span className="font-mono-numbers font-bold">{formatCurrency(stats.somma_delta_assoluta)}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grafico Andamento Giornaliero (solo se più di 1 giorno) */}
            {stats.giorni_nel_periodo > 1 && stats.incasso_per_data && stats.incasso_per_data.length > 1 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Andamento Incasso Giornaliero
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.incasso_per_data}>
                        <XAxis 
                          dataKey="data" 
                          tick={{ fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                          }}
                          interval={stats.incasso_per_data.length > 15 ? Math.floor(stats.incasso_per_data.length / 10) : 0}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => `€${v}`}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          labelFormatter={(label) => {
                            const date = new Date(label);
                            return format(date, 'EEEE d MMMM yyyy', { locale: it });
                          }}
                          labelStyle={{ color: '#333' }}
                        />
                        <Bar 
                          dataKey="incasso" 
                          fill="#10B981" 
                          radius={[2, 2, 0, 0]}
                          name="Incasso"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Incasso totale giorno per giorno nel periodo selezionato
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Incasso per giorno settimana */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Incasso Medio per Giorno
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weekdayChartData}>
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `€${v}`}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelStyle={{ color: '#333' }}
                      />
                      <Bar 
                        dataKey="incasso" 
                        fill="#D94F3D" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Ripartizione metodi pagamento */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Metodi di Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={paymentChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${percent.toFixed(0)}%`}
                        labelLine={false}
                      >
                        {paymentChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  {paymentChartData.map((item, idx) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="text-muted-foreground">{item.name}:</span>
                      <span className="font-mono-numbers">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Confronto periodo */}
            {confronta && stats.confronto && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    Confronto con {stats.confronto.periodo_precedente}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Incasso</span>
                      <div className="text-right">
                        <p className="font-mono-numbers">
                          {formatCurrency(stats.incasso_totale)} vs {formatCurrency(stats.confronto.incasso_precedente)}
                        </p>
                        <VariationBadge value={stats.confronto.incasso_variazione} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Coperti</span>
                      <div className="text-right">
                        <p className="font-mono-numbers">
                          {stats.coperti_totali} vs {stats.confronto.coperti_precedente}
                        </p>
                        <VariationBadge value={stats.confronto.coperti_variazione} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Spesa Media</span>
                      <div className="text-right">
                        <p className="font-mono-numbers">
                          {formatCurrency((stats.incasso_totale / stats.coperti_totali) || 0)} vs {formatCurrency(stats.confronto.spesa_media_precedente)}
                        </p>
                        <VariationBadge value={stats.confronto.spesa_media_variazione} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AnalyticsPage;
